import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Mic, Clock, Bell, LineChart, Flower } from "lucide-react";

const AppFeatures = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const features = [
    {
      title: "Guided Sessions ",
      // subtitle: "Expert-Led Instruction",
      description:
        "Experience deep restoration with our Yoga Nidra guided sessions, featuring instructions delivered in high-quality audio to enhance immersion and relaxation. Choose the one that best fits your needs - reduce stress, improve sleep, or support vitality and mental well-being.",
      // details:
      //   "• Sessions for all experience levels\n• Crystal-clear audio quality\n• Traditional Sanskrit guidance\n• Modern accessibility features",
      icon: <Flower className="w-6 h-6 text-blue-800" />,
      color: "forest-green",
      gradient: "from-blue-50 to-white",
    },
    {
      title: "Flexible Duration ",
      // subtitle: "Your Schedule, Your Peace",
      description:
        "Choose from sessions ranging from 15 to 35 minutes - ideal for a quick reset or a deeper practice, to fit into your schedule. Over time, you may find certain durations or times of day that resonate more, so follow what best supports your unique needs and natural rhythm. ",
      // details:
      //   "• 10, 20, 30, 45, 60 minute options\n• Progress tracking across sessions\n• Custom reminder settings\n• Offline download capability",
      icon: <Clock className="w-6 h-6 text-green-800" />,
      color: "copper-brown",
      gradient: "from-green-50 to-white",
    },
    {
      title: "Gentle Reminders ",
      // subtitle: "Gentle Practice Reminders",
      description:
        "Set practice reminders to help maintain a committed practice. This builds self-accountability and encourages to stick with the practice, even on busy, stressful or low- motivation days. Consistent practice rewires the brain for calm, focus and emotional balance.",
      // details:
      //   "• Customizable notification times\n• Gentle chime sounds\n• Inspirational daily quotes\n• Smart scheduling options",
      icon: <Bell className="w-6 h-6 text-orange-800" />,
      color: "soft-saffron",
      gradient: "from-orange-50 to-white",
    },
    {
      title: "Track your journey ",
      // subtitle: "Track Your Inner Journey",
      description:
        "Record feedback before and after a session and track your wellness journey. Tracking helps you notice gradual shifts that might otherwise go unnoticed, like - feeling calmer, more rested, breathing deeper or feeling emotionally resilient. ",
      // details:
      //   "• Weekly practice summaries\n• Streak tracking and rewards\n• Personal milestone celebrations\n• Meditation journal integration",
      icon: <LineChart className="w-6 h-6 text-green-800" />,
      color: "earth-ochre",
      gradient: "from-blue-50 to-green-50",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      id="features"
      className="relative py-16 overflow-hidden bg-white"
    >
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Modern Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            {/* Modern design border */}
            <div className="inline-block p-6 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl transform rotate-1 shadow-lg"></div>
              <div className="absolute inset-0 bg-gradient-to-l from-blue-50 to-green-50 rounded-2xl transform -rotate-1 shadow-lg"></div>

              <div className="relative bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-8 h-[2px] bg-gradient-to-r from-green-400 to-blue-400"></div>
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-[0.2em] font-mono">
                    Est. 2024
                  </span>
                  <div className="w-8 h-[2px] bg-gradient-to-l from-blue-400 to-green-400"></div>
                </div>

                <h2 className="font-heading text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
                  Cittasya App Features
                </h2>

                <div className="text-gray-500 font-medium uppercase tracking-wider text-xs">
                  ~ Designed for Inner Peace ~
                </div>
              </div>
            </div>

            <p className="text-base text-gray-700 max-w-2xl mx-auto leading-relaxed mt-6 font-light italic">
              " Discover the tools that will guide you to rejuvenation and inner peace through Yoga Nidra.   

Start your journey today - return to stillness within – anytime, anywhere.  "
            </p>
          </motion.div>

          {/* Single Row 4 Cards Layout */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card
                  className={`group relative overflow-hidden transition-all duration-500 h-full rounded-lg shadow-md hover:shadow-lg bg-gradient-to-br ${feature.gradient}`}
                >
                  <CardContent className="p-6 h-full flex flex-col">
                    {/* Top section with icon */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="relative">
                        {/* Icon container */}
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center shadow border border-gray-100 ${
                          index === 0 ? 'bg-blue-200' : 
                          index === 1 ? 'bg-green-200' : 
                          index === 2 ? 'bg-orange-200' : 
                          'bg-green-200'
                        }`}>
                          {feature.icon}
                        </div>
                      </div>

                      {/* Feature number */}
                      <div className="text-3xl font-bold text-gray-200 font-serif leading-none">
                        0{index + 1}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-gray-800 mb-2 text-green-600 leading-tight">
                        {feature.title}
                      </h3>

                      <p className="text-gray-600 leading-relaxed text-sm font-light">
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <p className="text-base text-gray-700 max-w-5xl mx-auto leading-relaxed mt-6 font-light italic text-center">
            "   

Yoga Nidra guides through multiple layers of awareness and works on the subtle layers of the mind and nervous system. Even short sessions can bring measurable physiological and psychological relief. Just 15 minutes of Yoga Nidra can be as restorative as an hour of conventional sleep."
          </p>
        </div>
      </div>
    </section>
  );
};

export default AppFeatures;
